import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Drawer, Empty, Image, Spin } from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  EditOutlined,
  FilePdfOutlined,
  IdcardOutlined,
  PhoneOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getCustomerRecord } from "../../api/user";
import UserRegister from "./UserRegister/UserRegister";
import "./CustomerInfoDrawer.css";

const IDENTITY_LABELS = {
  idCard: "身份证",
  passport: "护照",
  householdRegister: "户口本",
  borderResidentCard: "边境通行证",
  borderPass: "边境通行证",
  residencePermit: "居留证",
  driverLicense: "驾驶证",
  other: "其他证件",
};

const CONTACT_LABELS = {
  phone: "手机号",
  email: "邮箱",
  wechat: "微信",
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  telegram: "Telegram",
  other: "其他",
};

const COUNTRY_LABELS = {
  CN: "中国",
  LA: "老挝",
  VN: "越南",
  TH: "泰国",
  MM: "缅甸",
  US: "美国",
};

function formatCountries(values = []) {
  return values.map((value) => COUNTRY_LABELS[value] || value).join("、");
}

function maskValue(value) {
  const text = String(value || "");
  if (text.length <= 4) return "••••";
  return `${"•".repeat(Math.min(8, Math.max(4, text.length - 4)))}${text.slice(-4)}`;
}

function formatDate(value) {
  return value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DD") : "—";
}

function InfoRow({ label, children }) {
  return (
    <div className="customer-info-row">
      <span className="customer-info-label">{label}</span>
      <span className="customer-info-value">{children || "—"}</span>
    </div>
  );
}

export default function CustomerInfoDrawer({
  open,
  customerId,
  onClose,
  onUpdated,
}) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [revealed, setRevealed] = useState({});
  const requestIdRef = useRef(0);

  const loadCustomer = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!customerId) {
      setCustomer(null);
      setLoadError("");
      setLoading(false);
      return;
    }

    setCustomer(null);
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await getCustomerRecord(customerId);
      if (requestId === requestIdRef.current) {
        setCustomer(data);
        setRevealed({});
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setLoadError(error.response?.data?.message || "客户资料加载失败");
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (open) loadCustomer();
    else {
      requestIdRef.current += 1;
      setEditOpen(false);
      setRevealed({});
    }

    return () => {
      requestIdRef.current += 1;
    };
  }, [open, loadCustomer]);

  const handleUpdated = async (updatedUser) => {
    setEditOpen(false);
    await loadCustomer();
    onUpdated?.(updatedUser);
  };

  const toggleReveal = (key) => {
    setRevealed((current) => ({ ...current, [key]: !current[key] }));
  };

  const user = customer?.user || {};
  const identities = customer?.identities?.length
    ? customer.identities
    : customer?.identity
      ? [customer.identity]
      : [];
  const contacts = customer?.contacts || [];

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        placement="right"
        width="100%"
        closable={false}
        destroyOnClose={false}
        maskClosable={false}
        zIndex={1000}
        rootClassName="customer-info-drawer-root"
        styles={{ body: { padding: 0 } }}
      >
        <div className="customer-info-drawer">
          <header className="customer-info-header">
            <button
              className="customer-info-back"
              type="button"
              aria-label="关闭客户资料"
              onClick={onClose}
            >
              <ArrowLeftOutlined />
            </button>
            <h2>客户资料</h2>
            <Button
              type="text"
              icon={<EditOutlined />}
              disabled={!customer || loading}
              onClick={() => setEditOpen(true)}
            >
              编辑
            </Button>
          </header>

          <main className="customer-info-content">
            {loading && !customer ? (
              <div className="customer-info-loading"><Spin size="large" /></div>
            ) : loadError && !customer ? (
              <div className="customer-info-error">
                <Alert type="error" showIcon message={loadError} />
                <Button icon={<ReloadOutlined />} onClick={loadCustomer}>重新加载</Button>
              </div>
            ) : !customer ? (
              <Empty description="暂无客户资料" />
            ) : (
              <>
                {loadError && <Alert type="warning" showIcon message={loadError} />}
                <section className="customer-info-summary">
                  <div className="customer-info-avatar">
                    {(user.name || user.englishName || "客").slice(0, 1)}
                  </div>
                  <div className="customer-info-heading">
                    <div className="customer-info-name">{user.name || "未填写姓名"}</div>
                    <div className="customer-info-subtitle">
                      {user.englishName || "未填写英文名"}
                      {user.nationalities?.length > 0 && ` · ${formatCountries(user.nationalities)}`}
                    </div>
                  </div>
                </section>

                <div className="customer-info-sections">
                  <section className="customer-info-section">
                    <h3><IdcardOutlined /> 基本信息</h3>
                    <InfoRow label="姓名">{user.name || "—"}</InfoRow>
                    <InfoRow label="英文名">{user.englishName || "—"}</InfoRow>
                    <InfoRow label="性别">{{ male: "男", female: "女" }[user.gender] || "—"}</InfoRow>
                    <InfoRow label="出生日期">{formatDate(user.birthday)}</InfoRow>
                    <InfoRow label="国籍">{formatCountries(user.nationalities)}</InfoRow>
                  </section>

                  <section className="customer-info-section">
                    <h3><IdcardOutlined /> 证件信息 <span>{identities.length}</span></h3>
                    {identities.length ? identities.map((identity, index) => {
                      const identityKey = `identity-${identity._id || index}`;
                      const files = identity.files || [];
                      return (
                        <article className="customer-info-identity-card" key={identityKey}>
                          <div className="customer-info-identity-heading">
                            <strong>{IDENTITY_LABELS[identity.type] || identity.type || `证件 ${index + 1}`}</strong>
                            {identity.primary && <span className="customer-info-primary-tag">主要证件</span>}
                          </div>
                          <InfoRow label="证件号码">
                            {identity.number ? (
                              <>
                                <span>{revealed[identityKey] ? identity.number : maskValue(identity.number)}</span>
                                <button type="button" className="customer-info-reveal" onClick={() => toggleReveal(identityKey)}>
                                  {revealed[identityKey] ? "隐藏" : "显示"}
                                </button>
                              </>
                            ) : "—"}
                          </InfoRow>
                          <InfoRow label="签发国家">{COUNTRY_LABELS[identity.country] || identity.country}</InfoRow>
                          <InfoRow label="证件国籍">{COUNTRY_LABELS[identity.nationality] || identity.nationality}</InfoRow>
                          <InfoRow label="有效期">{formatDate(identity.expireDate)}</InfoRow>
                          <InfoRow label="证件附件">
                            {files.length ? (
                              <div className="customer-info-attachments" aria-label={`${IDENTITY_LABELS[identity.type] || "证件"}附件`}>
                                {files.map((file) => {
                                  const isImage = file.contentType?.startsWith("image/");
                                  const isPdf = file.contentType === "application/pdf";
                                  if (isImage) {
                                    return (
                                      <Image
                                        className="customer-info-attachment-image"
                                        key={file._id}
                                        src={file.signedUrl}
                                        alt={file.originalName || "证件图片"}
                                        width={82}
                                        height={82}
                                        preview={{ src: file.signedUrl }}
                                      />
                                    );
                                  }
                                  if (isPdf) {
                                    return (
                                      <a
                                        className="customer-info-attachment-pdf"
                                        key={file._id}
                                        href={file.signedUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={`新页面打开 ${file.originalName || "PDF 文件"}`}
                                        title={file.originalName || "PDF 文件"}
                                      >
                                        <FilePdfOutlined />
                                        <span>{file.originalName || "PDF 文件"}</span>
                                      </a>
                                    );
                                  }
                                  return null;
                                })}
                              </div>
                            ) : <span className="customer-info-attachment-empty">未上传</span>}
                          </InfoRow>
                        </article>
                      );
                    }) : <p className="customer-info-empty">未登记证件</p>}
                  </section>

                  <section className="customer-info-section">
                    <h3><PhoneOutlined /> 联系方式</h3>
                    {contacts.length ? contacts.map((contact, index) => {
                      const key = `contact-${contact._id || index}`;
                      const typeLabel = CONTACT_LABELS[contact.type] || contact.type;
                      const label = contact.label && contact.label !== typeLabel
                        ? `${typeLabel} · ${contact.label}`
                        : typeLabel;
                      return (
                        <InfoRow key={key} label={label}>
                          <span>
                            {revealed[key] ? contact.value : contact.type === "phone" ? maskValue(contact.value) : contact.value}
                            {contact.country ? ` · ${COUNTRY_LABELS[contact.country] || contact.country}` : ""}
                          </span>
                          {contact.type === "phone" && (
                            <button type="button" className="customer-info-reveal" onClick={() => toggleReveal(key)}>
                              {revealed[key] ? "隐藏" : "显示"}
                            </button>
                          )}
                        </InfoRow>
                      );
                    }) : <p className="customer-info-empty">未登记联系方式</p>}
                  </section>

                </div>

                <div className="customer-info-updated">
                  <CalendarOutlined /> 最近更新：{formatDate(user.updatedAt)}
                </div>
              </>
            )}
          </main>
        </div>
      </Drawer>

      <UserRegister
        open={editOpen}
        mode="edit"
        customerId={customerId}
        onClose={() => setEditOpen(false)}
        onSuccess={handleUpdated}
      />
    </>
  );
}
